/**
 * NotesList — MEDDPICC coverage strip, facet bar, selection toolbar, and the
 * note rows (clamped bodies, evidence reveal, invite, armed deletes).
 * Server state/actions come from useAccountNotes via props.
 */

import React, { useState } from 'react';
import {
  NOTE_TYPE_OPTIONS, NOTE_TYPE_LABEL, NOTE_VIS_OPTIONS,
  NOTE_SIGNALS, MEDDPICC_DIMS,
} from '@techstack/core';
import { useArmed } from './useAccountNotes';

function NotesList({ api, compact = false }) {
  const {
    allNotes, notes, dimCounts, packs, isLoading, loadError,
    fType, setFType, fSignal, setFSignal, fDim, setFDim, fText, setFText,
    searchHits, setSearchHits, searching, deepSearch,
    selected, selectedIds, toggleSelect, clearSelection,
    deleteNote, changeVis, setSharing, mergeNotes, addToPack,
  } = api;

  const [expanded, setExpanded] = useState({});
  const [inviteFor, setInviteFor] = useState(null);
  const [inviteText, setInviteText] = useState('');
  const [newPackOpen, setNewPackOpen] = useState(false);
  const [newPackName, setNewPackName] = useState('');
  const [facetsOpen, setFacetsOpen] = useState(false);
  const [armed, arm] = useArmed();

  const submitAddToPack = (packId, name) => {
    if (!selectedIds.length || addToPack.isPending) return;
    if (packId === '__new__' && !(name && name.trim())) return;
    addToPack.mutate({ packId, name, noteIds: selectedIds }, {
      onSuccess: () => { setNewPackOpen(false); setNewPackName(''); },
    });
  };

  const submitInvite = (n) => {
    const emails = inviteText.split(',').map((s) => s.trim()).filter(Boolean);
    setSharing.mutate({ id: n.id, emails }, {
      onSuccess: () => { setInviteFor(null); setInviteText(''); },
    });
  };

  const covered = MEDDPICC_DIMS.filter(([k]) => dimCounts[k] > 0)
    .sort(([a], [b]) => (dimCounts[b] || 0) - (dimCounts[a] || 0));
  const gapDims = MEDDPICC_DIMS.filter(([k]) => !dimCounts[k]);
  // Chip budget (audit §5): 4 visible + "+N" expander. An active filter
  // outside the top 4 keeps the row expanded so the selection stays visible.
  const FACET_CAP = 4;
  const capApplies = covered.length > FACET_CAP + 1 && !facetsOpen
    && !(fDim && covered.slice(FACET_CAP).some(([k]) => k === fDim));
  const visibleFacets = capApplies ? covered.slice(0, FACET_CAP) : covered;

  return (
    <>
      {/* MEDDPICC coverage, facets and the filter bar are working tools for the
          Notes tab. In a widget they are chrome around three notes, so compact
          renders the notes alone and the header's arrow opens the real thing. */}
      {!compact && allNotes.length > 0 && (covered.length === 0 ? (
        <p className="aw-notes-policy">MEDDPICC coverage appears after nightly enrichment.</p>
      ) : (
        <div className="aw-notes-facets">
          {visibleFacets.map(([k, lbl]) => (
            <button key={k} type="button"
              className={`aw-notes-chip aw-note-meddpicc${fDim === k ? ' aw-notes-chip-active' : ''}`}
              title={`${dimCounts[k]} clue(s) — click to filter`}
              onClick={() => { setFDim(fDim === k ? '' : k); setSearchHits(null); }}>
              {lbl} {dimCounts[k]}
            </button>
          ))}
          {capApplies && (
            <button type="button" className="aw-notes-chip"
              title="Show all MEDDPICC dimensions"
              onClick={() => setFacetsOpen(true)}>
              +{covered.length - visibleFacets.length}
            </button>
          )}
          {gapDims.length > 0 && (
            <span className="aw-notes-policy" title="No evidence captured yet — your discovery agenda">
              Gaps: {gapDims.map(([, lbl]) => lbl).join(' · ')}
            </span>
          )}
        </div>
      ))}

      {!compact && allNotes.length > 0 && (
        <div className="aw-notes-facets">
          <select className="aw-note-vis" value={fType} aria-label="Filter by note type"
            onChange={(e) => { setFType(e.target.value); setSearchHits(null); }}>
            <option value="">All types</option>
            {NOTE_TYPE_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select className="aw-note-vis" value={fSignal} aria-label="Filter by signal"
            onChange={(e) => { setFSignal(e.target.value); setSearchHits(null); }}>
            <option value="">All signals</option>
            {NOTE_SIGNALS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input
            className="aw-notes-filter-input"
            value={fText}
            placeholder="Filter… (Enter = deep search)"
            aria-label="Filter notes"
            onChange={(e) => { setFText(e.target.value); setSearchHits(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') deepSearch(); }}
          />
          <button type="button" className="aw-notes-chip" disabled={searching || !fText.trim()}
            title="Semantic search across enriched notes" onClick={deepSearch}>
            {searching ? 'Searching…' : 'Deep search'}
          </button>
          {searchHits && (
            <button type="button" className="aw-notes-chip" onClick={() => { setSearchHits(null); setFText(''); }}>
              Clear ({searchHits.mode})
            </button>
          )}
        </div>
      )}

      {/* Selection toolbar — persistent while anything is selected. */}
      {selectedIds.length > 0 && (
        <div className="aw-notes-toolbar">
          <strong>{selectedIds.length} selected</strong>
          <button type="button" className="aw-notes-save" disabled={mergeNotes.isPending || selectedIds.length < 2}
            title={selectedIds.length < 2 ? 'Select at least two notes to merge' : 'Merge into one consolidated note'}
            onClick={() => mergeNotes.mutate(selectedIds)}>
            {mergeNotes.isPending ? 'Merging…' : 'Merge'}
          </button>
          <select className="aw-note-vis" value="" disabled={addToPack.isPending}
            aria-label="Add selected notes to a Context Pack"
            onChange={(e) => {
              if (e.target.value === '__new__') setNewPackOpen(true);
              else if (e.target.value) submitAddToPack(e.target.value);
            }}>
            <option value="">{addToPack.isPending ? 'Adding…' : 'Add to pack…'}</option>
            {packs.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.itemCount})</option>)}
            <option value="__new__">New pack…</option>
          </select>
          {newPackOpen && (
            <>
              <input className="aw-notes-filter-input" value={newPackName} placeholder="New pack name"
                aria-label="New pack name" autoFocus
                onChange={(e) => setNewPackName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitAddToPack('__new__', newPackName); }} />
              <button type="button" className="aw-notes-save" disabled={addToPack.isPending || !newPackName.trim()}
                onClick={() => submitAddToPack('__new__', newPackName)}>Create & add</button>
              <button type="button" className="aw-notes-chip" onClick={() => { setNewPackOpen(false); setNewPackName(''); }}>Cancel</button>
            </>
          )}
          <span className="aw-notes-spacer" />
          <button type="button" className="aw-notes-chip" onClick={clearSelection}>Clear</button>
        </div>
      )}

      {isLoading && <p className="aw-none">Loading notes…</p>}
      {loadError && <p className="aw-notes-error">{loadError.message || 'Could not load notes (assigned team only).'}</p>}
      {!isLoading && !loadError && notes.length === 0 && (
        <p className="aw-none">{allNotes.length ? 'Nothing matches the filters.' : 'No notes yet — dump the first one above.'}</p>
      )}

      <div className="aw-notes-list">
        {notes.map((n) => (
          <div key={n.id} className="aw-note ts-row">
            <div className="aw-note-row">
              {!compact && (
              <input
                type="checkbox"
                checked={!!selected[n.id]}
                onChange={() => toggleSelect(n.id)}
                aria-label="Select note"
                title="Select for merge / packs"
              />
              )}
              <div className="aw-note-main">
                <div className={`aw-note-body${expanded[n.id] ? ' aw-note-body-open' : ''}`}>
                  {n.title ? <strong>{n.title} — </strong> : null}
                  {n.content}
                </div>
                {!compact && ((n.content || '').length > 240 || (n.content || '').split('\n').length > 3) && (
                  <button type="button" className="aw-note-more"
                    onClick={() => setExpanded((x) => ({ ...x, [n.id]: !x[n.id] }))}>
                    {expanded[n.id] ? 'Show less' : 'Show more'}
                  </button>
                )}
                {!compact && (n.aiSuggestedActions?.actionItems || []).map((a, i) => (
                  <div key={i} className="aw-note-action">
                    ☐ {a.description}{a.owner ? ` — ${a.owner}` : ''}{a.due ? ` (due ${a.due})` : ''}
                  </div>
                ))}
                <div className="aw-note-meta">
                  {!compact && <span className="aw-chip">{NOTE_TYPE_LABEL[n.type] || n.type}</span>}
                  {!compact && MEDDPICC_DIMS.filter(([k]) => ((n.meddpicc || {})[k] || []).length).map(([k, lbl]) => (
                    <button key={k} type="button" className="aw-note-signal aw-note-meddpicc"
                      title="Show the extracted evidence"
                      onClick={() => setExpanded((x) => ({ ...x, ['ev' + n.id]: !x['ev' + n.id] }))}>
                      {lbl}
                    </button>
                  ))}
                  {!compact && (n.signals || []).map((s) => <span key={s} className="aw-note-signal">{s}</span>)}
                  {!compact && (n.tags || []).map((t) => <span key={t} className="aw-note-tag">#{t}</span>)}
                  {n.status === 'consolidated' && <span className="aw-chip aw-note-consolidated">consolidated</span>}
                  {n.similarity != null && <span className="aw-note-tag">{Math.round(n.similarity * 100)}% match</span>}
                  <span className="aw-note-author">
                    {n.author || 'Sales AI'}{n.createdAt ? ` · ${String(n.createdAt).slice(0, 10)}` : ''}
                  </span>
                  <span className="aw-notes-spacer" />
                  {/* Author controls reveal on row hover (audit §5) */}
                  {!compact && (
                  <span className="ts-row-actions">
                    {n.mine && n.visibility === 'team' && (
                      <button type="button" className="aw-notes-chip"
                        title={(n.sharedWith || []).length ? `${n.sharedWith.length} teammate(s) invited` : 'Invite teammates into this note'}
                        onClick={() => { setInviteFor(inviteFor === n.id ? null : n.id); setInviteText((n.sharedWith || []).join(', ')); }}>
                        Invite{(n.sharedWith || []).length ? ` (${n.sharedWith.length})` : ''}
                      </button>
                    )}
                    {n.mine && (
                      <select
                        className="aw-note-vis"
                        value={n.visibility || 'team'}
                        aria-label="Note visibility"
                        title="Visibility (author only)"
                        onChange={(e) => changeVis.mutate({ id: n.id, visibility: e.target.value })}
                      >
                        {NOTE_VIS_OPTIONS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                      </select>
                    )}
                    {n.mine && (
                      <button
                        type="button"
                        className={`aw-notes-chip${armed['n' + n.id] ? ' aw-armed ts-armed' : ''}`}
                        title="Delete permanently (hard delete)"
                        onClick={() => arm('n' + n.id, () => deleteNote.mutate(n.id))}
                      >
                        {armed['n' + n.id] ? 'Sure?' : 'Delete'}
                      </button>
                    )}
                  </span>
                  )}
                </div>
                {expanded['ev' + n.id] && (
                  <div className="aw-note-evidence">
                    {MEDDPICC_DIMS.filter(([k]) => ((n.meddpicc || {})[k] || []).length).map(([k, lbl]) =>
                      ((n.meddpicc || {})[k] || []).map((c, i) => (
                        <div key={k + i}><strong>{lbl}:</strong> {c.evidence}{c.person ? ` (${c.person})` : ''}{c.competitor ? ` (${c.competitor})` : ''}</div>
                      )))}
                  </div>
                )}
                {inviteFor === n.id && (
                  <div className="aw-notes-facets" style={{ marginTop: 4 }}>
                    <input className="aw-notes-filter-input" value={inviteText} placeholder="email, email…"
                      aria-label="Invite emails" autoFocus
                      onChange={(e) => setInviteText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') submitInvite(n); }} />
                    <button type="button" className="aw-notes-save" onClick={() => submitInvite(n)}>Share</button>
                    <button type="button" className="aw-notes-chip" onClick={() => setInviteFor(null)}>Cancel</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export default NotesList;
